import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  listDevices, getPreferredDevice, speakerSelectionSupported,
  type DeviceKind, type DeviceOption,
} from '@/lib/mediaDevices';

/**
 * Welle 8c-pre: kleines Pfeil-Symbol neben Mic/Kamera/Lautsprecher.
 * Öffnet eine Liste der verfügbaren Geräte; Auswahl wird gemerkt (localStorage)
 * und per onSelect angewandt (Track-Wechsel bzw. setSinkId).
 *
 * pre162: Pfeil ist jetzt ein klar sichtbarer weißer Chevron (vorher dunkles
 * Unicode-Zeichen, kaum sichtbar). Beim Lautsprecher kann zusätzlich ein
 * Lautstärke-Regler eingeblendet werden (volume/onVolume).
 *
 * Das Dropdown wird per Portal an document.body gerendert (position: fixed),
 * damit es keinen Stacking-Context der Toolbar erbt und nicht hinter der
 * linken Spalte verschwindet.
 */
export function DeviceMenu({
  kind, title, onSelect, volume, onVolume,
}: {
  kind: DeviceKind;
  title: string;
  onSelect: (deviceId: string) => void;
  volume?: number;
  onVolume?: (v: number) => void;
}) {
  const [open, setOpen] = useState(false);
  const [devs, setDevs] = useState<DeviceOption[]>([]);
  const [current, setCurrent] = useState<string | null>(getPreferredDevice(kind));
  const [pos, setPos] = useState<{ left: number; bottom: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const unsupported = kind === 'audiooutput' && !speakerSelectionSupported();
  const showVolume = typeof volume === 'number' && !!onVolume;
  // Lautsprecher-Menü darf trotz fehlendem setSinkId aufgehen, wenn es einen
  // Lautstärke-Regler anzubieten hat.
  const disabled = unsupported && !showVolume;

  useEffect(() => {
    if (!open) return;
    listDevices().then((d) => setDevs(d[kind]));
    const r = btnRef.current?.getBoundingClientRect();
    if (r) setPos({ left: r.left + r.width / 2, bottom: window.innerHeight - r.top + 6 });
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (btnRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open, kind]);

  return (
    <>
      <button
        ref={btnRef}
        title={disabled ? `${title} (in diesem Browser nicht verfügbar)` : title}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        style={{
          width: 16, height: 28, border: 'none', background: 'transparent',
          cursor: disabled ? 'default' : 'pointer',
          opacity: disabled ? 0.3 : 0.9, padding: 0, marginLeft: -5,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="#ffffff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {open && pos && createPortal(
        <div
          ref={menuRef}
          style={{
            position: 'fixed', left: pos.left, bottom: pos.bottom, transform: 'translateX(-50%)',
            minWidth: 220, maxWidth: 340, background: '#1c1c1a', color: '#f5f5f0',
            border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, padding: 4,
            boxShadow: '0 8px 24px rgba(0,0,0,0.5)', zIndex: 1000, textAlign: 'left',
          }}
        >
          <div style={{ fontSize: 10, opacity: 0.5, padding: '4px 8px', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {title}
          </div>

          {unsupported && (
            <div style={{ fontSize: 11, opacity: 0.6, padding: '2px 8px 6px' }}>
              Geräteauswahl in diesem Browser nicht verfügbar.
            </div>
          )}
          {!unsupported && devs.length === 0 && (
            <div style={{ fontSize: 12, opacity: 0.6, padding: '6px 8px' }}>Keine Geräte gefunden</div>
          )}
          {!unsupported && devs.map((d) => (
            <button
              key={d.deviceId}
              onClick={() => { onSelect(d.deviceId); setCurrent(d.deviceId); setOpen(false); }}
              style={{
                display: 'block', width: '100%', textAlign: 'left', border: 'none',
                background: 'transparent', color: 'inherit', cursor: 'pointer',
                padding: '6px 8px', borderRadius: 5, fontSize: 12,
                fontWeight: current === d.deviceId ? 700 : 400,
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.08)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              {current === d.deviceId ? '● ' : '○ '}{d.label}
            </button>
          ))}

          {showVolume && (
            <div style={{ padding: '6px 8px 4px' }}>
              {(unsupported || devs.length > 0) && <div style={{ height: 1, background: 'rgba(255,255,255,0.1)', margin: '2px 0 8px' }} />}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 11, opacity: 0.7, marginBottom: 6 }}>
                <span>Lautstärke</span>
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>{Math.round((volume as number) * 100)}%</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <svg viewBox="0 0 24 24" width="14" height="14" fill="#f5f5f0" style={{ flexShrink: 0, opacity: 0.8 }}>
                  <path d="M3 9v6h4l5 5V4L7 9H3z" />
                </svg>
                <input
                  type="range" min={0} max={1} step={0.01} value={volume}
                  onChange={(e) => onVolume!(parseFloat(e.target.value))}
                  style={{ flex: 1, accentColor: '#E2001A', cursor: 'pointer' }}
                />
              </div>
            </div>
          )}
        </div>,
        document.body,
      )}
    </>
  );
}
